import { Carousel, Slide } from '@uhg-abyss/web/ui/Carousel';
import React, { useContext, useEffect, useState } from 'react';

import { CountySearchContext } from '../../context/CountySearchContext';
import { PortalContext } from '../../context/PortalContext';
import { useCoverageType } from '../../hooks/useCoverageType';
import { useLagoon } from '../../hooks/useLagoon';
import { useSmartChoice } from '../../hooks/useSmartChoice';
import { useSnackcardViewAllCount } from '../../hooks/useSnackcardViewAllCount';
import { Provider } from '../../models/Provider';
import { ProviderType } from '../../models/ProviderDetails';
import { ResponseHeaders } from '../../models/ResponseHeaders';
import { isProviderStartDateInFuture } from '../../utils/date.utils';
import {
  returnSuppressFlag,
  suppressCostEstimatesForPolicies,
} from '../../utils/featureSuppress';
import { getShowCost } from '../../utils/showCost.utils';
import { getCurrentMember } from '../../utils/user.utils';
import { Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { DataCardContainer } from '../DataCard/DataCardContainer';
import {
  getMaxBadgeCount,
  isIFPOrganizationCheck,
  shouldDisplayVirtualVisit,
  shouldSuppressVirtualCare,
} from '../DataCard/DataCardUtils';
import { OutsideCountyAlert } from '../DataCard/OutsideCountyAlert';
import { NationalGroupProvidersVirtualDataCard } from '../NationalGroupProvidersVirtualBiteCard/NationalGroupProvidersVirtualDataCard';
import { getGeoLocationFromStorage } from '../PSXHeader/SearchBar/utils';
import {
  SlideContentWrapper,
  mixedResultsSlideStyle,
  slideStyle,
} from '../SnackCard/SnackCard.styles';
import { isOutOfCounty } from '../SnackCardContainer/utils';
import { getFeatureFlag, getFeatureFlags } from '../Utils';
import { makeAllSlidesAriaHidden } from '../Utils/a11yUtils/a11yUtils';
import { isMnrMember } from '../Utils/memberUtils/memberUtils';
import { checkTerminationDate } from '../Utils/terminateDateIndicatorUtils';
import { ResultCardViewAllLinks } from './ResultCardsViewAllLink';
import { DataCardStyle, mixedResultCarouselStyle } from './ResultSection.style';

type Props = {
  headers: ResponseHeaders;
  results: Provider[];
  sectionType: string;
  isLoading: boolean;
  searchTerm: string;
  searchRadius?: number;
  handleViewAllOnClick?: () => void;
  isTieredPlan?: boolean;
};

export const MixedResultCards = ({
  headers,
  results,
  sectionType,
  isLoading,
  searchTerm,
  handleViewAllOnClick,
  searchRadius,
  isTieredPlan = false,
}: Props) => {
  const result = results?.slice(
    0,
    Constants.RESULT_SECTION.MAX_PROVIDERS_TO_DISPLAY_IN_MIXED_RESULTS_SCREEN
  );
  const featureFlags = useLagoon('feature-flags')();
  const countySearchAlertFlag: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_COUNTY_SEARCH_ALERT
  );
  const { isExpandViewAllEnabled } = useSnackcardViewAllCount();
  const { isCountySearch } = useContext(CountySearchContext);
  const { name, stateCode } = getGeoLocationFromStorage();
  const [slidesPerView, setSlidesPerView] = useState(2);
  const [winWidth, setWinWidth] = useState(window.innerWidth);
  const { currentMember, lineOfBusiness } = getCurrentMember();
  const isMNR = isMnrMember(lineOfBusiness);
  const coverageType = useCoverageType();
  const { portalData } = useContext(PortalContext);
  const portalShowCost = getShowCost(portalData);

  useEffect(() => {
    let windowWidth = window.innerWidth;
    function handleResize() {
      windowWidth = window.innerWidth;
      setWinWidth(windowWidth);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    if (winWidth > 872) {
      if (winWidth > 872 && result?.length >= 3) setSlidesPerView(2);
      else if (winWidth < 1250 && result?.length === 2) setSlidesPerView(3);
      else if (winWidth < 1250 && result?.length > 2) setSlidesPerView(2);
      else if (winWidth > 1250 && result?.length >= 2) setSlidesPerView(3);
      else if (result?.length >= 1) setSlidesPerView(4);
    }
    if (winWidth < 872) setSlidesPerView(1);
    if (
      isExpandViewAllEnabled &&
      winWidth > 868 &&
      (result?.length === 1 || result?.length === 2)
    )
      setSlidesPerView(2);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [winWidth, result, isExpandViewAllEnabled]);

  const displayVirtualProvider = (provider: Provider) =>
    !provider?.address &&
    !!provider?.virtualCareOffered?.length &&
    (provider.providerType === ProviderType.PRACTITIONER ||
      provider.providerType === ProviderType.PROVIDER_GROUP);

  const smartChoiceEligibility = useSmartChoice();
  const isSmartChoiceForAll = smartChoiceEligibility.isAvailable;
  const isSmartChoice = smartChoiceEligibility.isEnabled;

  const [
    displayTerminationDateEnabled,
    enableCost,
    isPlatinumProviderIdentifierEnabled,
    virtualCareIndicatorEnabled,
  ] = getFeatureFlags(featureFlags, [
    ConstantsLagoon.FEATURE_FLAGS.DISPLAY_FUTURE_TERMINATION_DATE,
    ConstantsLagoon.FEATURE_FLAGS.COST_ENABLED,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PLATINUM_PROVIDER_IDENTIFIER,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BCC_FILTER,
    ConstantsLagoon.FEATURE_FLAGS.VIRTUAL_CARE_INDICATOR_ENABLED,
  ]);

  const isVirtualCareQuickCareSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_QUICK_CARE
  );

  const isVirtualCareUnetRailroadSuppressed = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_RAILROAD
  );

  const suppressVirtualCare = shouldSuppressVirtualCare(
    isVirtualCareQuickCareSuppressed,
    coverageType,
    isVirtualCareUnetRailroadSuppressed
  );

  const suppressCostEstimates = suppressCostEstimatesForPolicies(
    coverageType,
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.COST_ESTIMATES,
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.COST_ESTIMATES_EMPIRE
  );

  const displayVirtualVisit = result?.some((res) =>
    shouldDisplayVirtualVisit(
      virtualCareIndicatorEnabled,
      suppressVirtualCare,
      res?.address,
      isIFPOrganizationCheck(res?.providerType, currentMember?.isIFPMember),
      !!res?.virtualCareOffered?.length,
      res?.providerType
    )
  );

  const shouldDisplayNetworkStatus = result?.some((res) => res?.networkStatus);

  const maxBadgeCount = getMaxBadgeCount(
    result,
    isPlatinumProviderIdentifierEnabled,
    displayTerminationDateEnabled,
    displayVirtualVisit,
    !suppressCostEstimates && enableCost && portalShowCost,
    shouldDisplayNetworkStatus
  );

  const slides = () => {
    if (isLoading) {
      return [1, 2, 3].map((index) => (
        <Slide css={slideStyle(index)} key={index}>
          <SlideContentWrapper>
            <DataCardContainer
              css={DataCardStyle}
              index={index}
              isLoading
              searchTerm={searchTerm}
              sectionType={sectionType}
            />
          </SlideContentWrapper>
        </Slide>
      ));
    }

    const isDisplayVirtualProvider = result?.some((res) =>
      displayVirtualProvider(res)
    );

    const isNetworkAlertIndicatorEnabled = result?.some((snackCardProvider) =>
      checkTerminationDate(
        displayTerminationDateEnabled,
        snackCardProvider?.networkEndDate
      )
    );
    const isInNetworkBadgeEnabled = result?.some((snackCardProvider) =>
      isProviderStartDateInFuture(snackCardProvider?.networkStartDate)
    );

    const isOutOfCntyIndicatorEnabled = result?.some((res) =>
      isOutOfCounty(isCountySearch, name, stateCode, res)
    );

    const outOfCntyIndicatoryShowing =
      countySearchAlertFlag && isOutOfCntyIndicatorEnabled;

    const isSmartChoiceScoreBadge = result.some(
      (snackCardProvider) =>
        parseInt(
          snackCardProvider?.recommendationDetails?.totalRecommendationScore ||
            '0'
        ) > 0
    );

    const allSlides = result?.map((res, i) => {
      const isOutOfCnty = isOutOfCounty(isCountySearch, name, stateCode, res);
      const showOutOfCntyIndicatory =
        (isOutOfCnty && countySearchAlertFlag) || isOutOfCntyIndicatorEnabled;
      const isNetworkAlertIndicator = result.some((snackCardProvider) =>
        checkTerminationDate(
          displayTerminationDateEnabled,
          snackCardProvider?.networkEndDate
        )
      );
      const isInNetworkBadge = result.some((snackCardProvider) =>
        isProviderStartDateInFuture(snackCardProvider?.networkStartDate)
      );

      return (
        <Slide
          css={mixedResultsSlideStyle(
            i,
            result?.length,
            isExpandViewAllEnabled
          )}
          data-testid={`mixed-result-card-${i}`}
          key={i}
        >
          <SlideContentWrapper>
            {!displayVirtualProvider(res) && (
              <React.Fragment>
                <DataCardContainer
                  css={DataCardStyle}
                  headers={headers}
                  index={result.indexOf(res)}
                  isInNetworkBadge={isInNetworkBadge}
                  isLoading={isLoading}
                  isMNR={isMNR}
                  isNetworkAlertIndicator={isNetworkAlertIndicator}
                  isTieredPlan={isTieredPlan}
                  locationForAnalytics={`${sectionType} results`}
                  maxBadgeCount={maxBadgeCount}
                  practitioner={res}
                  providerList={result}
                  searchRadius={searchRadius}
                  searchTerm={searchTerm}
                  sectionType={sectionType}
                />
                {showOutOfCntyIndicatory && (
                  <OutsideCountyAlert
                    county={name?.split(',')[0]}
                    isOutOfCntyIndicatorEnabled={isOutOfCntyIndicatorEnabled}
                    isOutOfCounty={isOutOfCnty}
                  />
                )}
              </React.Fragment>
            )}
            {displayVirtualProvider(res) && (
              <React.Fragment>
                <NationalGroupProvidersVirtualDataCard
                  css={DataCardStyle}
                  fromMobileListView={false}
                  headers={headers}
                  index={result.indexOf(res)}
                  isLoading={isLoading}
                  locationForAnalytics={`${sectionType} results`}
                  practitioner={res}
                  sectionType={sectionType}
                />

                {showOutOfCntyIndicatory && (
                  <OutsideCountyAlert
                    county={name?.split(',')[0]}
                    isOutOfCntyIndicatorEnabled={isOutOfCntyIndicatorEnabled}
                    isOutOfCounty={isOutOfCnty}
                  />
                )}
              </React.Fragment>
            )}
          </SlideContentWrapper>
        </Slide>
      );
    });

    {
      isExpandViewAllEnabled &&
        allSlides.push(
          <ResultCardViewAllLinks
            handleViewAllOnClick={handleViewAllOnClick}
            isDisplayVirtualProvider={isDisplayVirtualProvider}
            isNetworkIndicatorEnabled={
              isInNetworkBadgeEnabled || isNetworkAlertIndicatorEnabled
            }
            isOutOfCntyIndicatorEnabled={isOutOfCntyIndicatorEnabled}
            isSmartChoice={isSmartChoiceForAll}
            isSmartChoiceScoreBadge={isSmartChoiceScoreBadge && isSmartChoice}
            outOfCntyIndicatoryShowing={outOfCntyIndicatoryShowing}
            resultCount={result?.length}
            sectionType={sectionType}
          />
        );
    }

    return allSlides;
  };

  makeAllSlidesAriaHidden();

  return (
    <React.Fragment>
      <Carousel
        css={
          mixedResultCarouselStyle(
            isExpandViewAllEnabled ? result?.length + 1 : result?.length,
            winWidth,
            isLoading,
            isExpandViewAllEnabled
          ) as any
        }
        data-testid="mixed-results-carousel-skeleton-loader"
        label=""
        nextSlideOnClick={() => makeAllSlidesAriaHidden()}
        prevSlideOnClick={() => makeAllSlidesAriaHidden()}
        slideIndexOnClick={() => makeAllSlidesAriaHidden()}
        slides={slides()}
        slidesPerView={slidesPerView}
      />
    </React.Fragment>
  );
};
